import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  IconAlertCircle,
  IconChevronRight,
  IconExternalLink,
  IconSchool,
  IconSchoolOff,
} from '@rossum/ui/icons/tabler';
import {
  Box,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import {
  NeighborAnnotationMetadata,
  useNeighborAnnotationContent,
  useNeighborPagePreview,
} from '../core';
import { FieldHighlightOverlay } from './FieldHighlightOverlay';

// A4 portrait, so the placeholder doesn't jump once the real page loads.
const FALLBACK_ASPECT_RATIO = '1 / 1.414';

/**
 * The part of a metadata line that carries the actual value. Set apart by
 * colour alone — the labels around it stay on `text.secondary`.
 */
const Value = (chunks: ReactNode[]) => (
  <Box component="span" sx={{ color: 'text.primary' }}>
    {chunks}
  </Box>
);

type NeighborCardProps = {
  /**
   * `reviewed` is the page the neighbors were found for. Its filename is
   * already in the top bar and its own edit history says nothing about the
   * training decision, so it shows a heading naming the relationship instead.
   */
  variant: 'reviewed' | 'neighbor';
  annotationId: number | null;
  pageNumber: number;
  focusedSchemaId: string | null;
  metadata: NeighborAnnotationMetadata | undefined;
  similarity?: number;
  link?: string;
  isLoading: boolean;
  onToggleTraining: (annotationId: number, enabled: boolean) => void;
};

export const NeighborCard = ({
  variant,
  annotationId,
  pageNumber,
  focusedSchemaId,
  metadata,
  similarity,
  link,
  isLoading,
  onToggleTraining,
}: NeighborCardProps) => {
  const intl = useIntl();
  const [isImageError, setIsImageError] = useState(false);
  const isReviewed = variant === 'reviewed';

  const {
    previewUrl,
    pageWidth,
    pageHeight,
    isLoading: previewLoading,
  } = useNeighborPagePreview(annotationId, pageNumber);

  const { datapoints } = useNeighborAnnotationContent(annotationId, pageNumber);

  const fileName =
    metadata?.fileName ??
    intl.formatMessage({ id: 'components.neighborsDrawer.item.unknown' });

  const aspectRatio =
    pageWidth && pageHeight
      ? `${pageWidth} / ${pageHeight}`
      : FALLBACK_ASPECT_RATIO;

  const isExcluded = metadata ? !metadata.trainingEnabled : false;

  // A page reaching its final state by export outranks the plain edit stamp.
  const activity = metadata?.exportedAt
    ? {
        id: 'features.documentNeighbors.card.exported' as const,
        at: metadata.exportedAt,
      }
    : metadata?.modifiedAt
      ? {
          id: 'features.documentNeighbors.card.lastEdited' as const,
          at: metadata.modifiedAt,
        }
      : null;

  const showPreview = previewUrl && !isImageError;
  const isPreviewLoading = isLoading || previewLoading;

  return (
    <Stack
      sx={{
        width: 1,
        minWidth: 0,
        // Reveals the open-document affordance only while the card is hovered.
        '&:hover .document-link-icon': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 1,
          // Page fits the column width; height follows from the page proportions.
          aspectRatio,
          border: t => `1px solid ${t.palette.divider}`,
          borderRadius: borderRadius('l'),
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          // An excluded page stays readable but visibly steps back.
          opacity: isExcluded ? 0.4 : 1,
          filter: isExcluded ? 'grayscale(1)' : 'none',
          transition: t =>
            t.transitions.create(['opacity', 'filter'], {
              duration: t.transitions.duration.short,
            }),
        }}
      >
        {showPreview ? (
          <>
            <Box
              component="img"
              src={previewUrl}
              alt={fileName}
              data-cy="neighbor-page-preview"
              sx={{ display: 'block', width: 1, height: 1 }}
              onError={() => setIsImageError(true)}
            />
            <FieldHighlightOverlay
              datapoints={datapoints}
              pageWidth={pageWidth}
              pageHeight={pageHeight}
              focusedSchemaId={focusedSchemaId}
            />
          </>
        ) : (
          <Stack
            spacing={1}
            useFlexGap
            sx={{
              height: 1,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {isPreviewLoading ? (
              <CircularProgress size={16} />
            ) : (
              <>
                <SvgIcon fontSize="medium">
                  <IconAlertCircle />
                </SvgIcon>
                <Typography variant="caption" color="text.secondary">
                  {intl.formatMessage({
                    id: 'components.neighborsDrawer.item.imageNotFound',
                  })}
                </Typography>
              </>
            )}
          </Stack>
        )}
      </Box>

      {/* Close under the page and inset from its edges, so it reads as part of it. */}
      <Stack sx={{ mt: 1, px: 2, minWidth: 0 }}>
        {/* Title left, action right — the edit screen's part-header pattern. */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          {isReviewed ? (
            <Typography variant="h6" noWrap>
              {intl.formatMessage({
                id: 'features.documentNeighbors.card.reviewedHeading',
              })}
            </Typography>
          ) : link ? (
            <Stack
              direction="row"
              component={Link}
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
              spacing={1}
              useFlexGap
              sx={{ alignItems: 'center', minWidth: 0 }}
            >
              <Typography variant="h6" noWrap>
                {fileName}
              </Typography>
              <SvgIcon
                className="document-link-icon"
                fontSize="small"
                sx={{
                  flexShrink: 0,
                  opacity: 0,
                  transition: t =>
                    t.transitions.create('opacity', {
                      duration: t.transitions.duration.shortest,
                    }),
                }}
              >
                <IconExternalLink />
              </SvgIcon>
            </Stack>
          ) : (
            <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
              {fileName}
            </Typography>
          )}

          {annotationId && metadata ? (
            <Tooltip
              title={intl.formatMessage({
                id: isExcluded
                  ? 'components.sidebarV2.annotationData.trainingToggle.action.include'
                  : 'components.sidebarV2.annotationData.trainingToggle.action.exclude',
              })}
            >
              {/* span keeps the tooltip working while the button is disabled */}
              <span>
                <IconButton
                  size="small"
                  color={isExcluded ? 'default' : 'success'}
                  disabled={!metadata.canToggleTraining}
                  onClick={() => onToggleTraining(annotationId, isExcluded)}
                  data-cy="neighbor-toggle-training"
                  sx={{ flexShrink: 0 }}
                >
                  <SvgIcon fontSize="medium">
                    {isExcluded ? <IconSchoolOff /> : <IconSchool />}
                  </SvgIcon>
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
        </Stack>

        {similarity !== undefined ? (
          <Typography variant="body2" color="text.secondary" noWrap>
            {intl.formatMessage(
              { id: 'features.documentNeighbors.card.similarity' },
              { similarity: Math.round(similarity * 100), value: Value }
            )}
          </Typography>
        ) : null}

        {/* Workspace › queue, as `document-list/components/BreadCrumbs`. */}
        {metadata?.queueName ? (
          <Stack
            direction="row"
            sx={{ alignItems: 'center', minWidth: 0, maxWidth: 1 }}
          >
            {metadata.workspaceName ? (
              <>
                <Typography
                  variant="body2"
                  color="text.primary"
                  noWrap
                  sx={{ minWidth: 0 }}
                >
                  {metadata.workspaceName}
                </Typography>
                <SvgIcon
                  fontSize="medium"
                  color="disabled"
                  sx={{ mx: 0.5, flexShrink: 0 }}
                >
                  <IconChevronRight />
                </SvgIcon>
              </>
            ) : null}
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ minWidth: 0 }}
            >
              {metadata.queueName}
            </Typography>
          </Stack>
        ) : null}

        {!isReviewed && activity ? (
          <Tooltip
            title={intl.formatDate(activity.at, {
              dateStyle: 'full',
              timeStyle: 'short',
            })}
            placement="bottom-start"
          >
            {/* Date leads so it can be scanned down the column. */}
            <Typography variant="body2" color="text.secondary" noWrap>
              {intl.formatMessage(
                { id: activity.id },
                {
                  date: intl.formatDate(activity.at, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  }),
                  value: Value,
                }
              )}
            </Typography>
          </Tooltip>
        ) : null}

        {!isReviewed && metadata?.modifier ? (
          <Typography variant="body2" color="text.secondary" noWrap>
            {metadata.modifier.email
              ? intl.formatMessage(
                  { id: 'features.documentNeighbors.card.editor' },
                  {
                    name: metadata.modifier.name,
                    email: metadata.modifier.email,
                    value: Value,
                  }
                )
              : Value([metadata.modifier.name])}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};
